/**
 * Vitis Predictive Search Component
 * Custom Element for Shopify Predictive Search API
 * Based on page.tsx React design
 */

/**
 * Algolia Search Integration
 * Dynamically loads Algolia Search Lite and initializes the index
 */
var vitisAlgoliaIndex = null;
var vitisAlgoliaReady = false;

(function initAlgolia() {
  // Check if Algolia is already loaded
  if (typeof window.algoliasearch !== 'undefined') {
    initAlgoliaClient();
    return;
  }
  
  // Check if script is already being loaded
  if (document.querySelector('script[src*="algoliasearch"]')) {
    waitForAlgolia();
    return;
  }
  
  // Dynamically inject Algolia Search Lite script
  var script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/algoliasearch@4/dist/algoliasearch-lite.umd.js';
  script.async = true;
  script.onload = function() {
    initAlgoliaClient();
  };
  script.onerror = function() {
    console.error('[Vitis Algolia] Failed to load Algolia script');
  };
  document.head.appendChild(script);
  
  function waitForAlgolia() {
    var attempts = 0;
    var maxAttempts = 50; // 5 seconds max
    var checkInterval = setInterval(function() {
      attempts++;
      if (typeof window.algoliasearch !== 'undefined') {
        clearInterval(checkInterval);
        initAlgoliaClient();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error('[Vitis Algolia] Timeout waiting for algoliasearch');
      }
    }, 100);
  }
  
  function initAlgoliaClient() {
    try {
      var client = window.algoliasearch('U8HGSUH4L4', '1b534a81492820c638f945abc76d42b0');
      vitisAlgoliaIndex = client.initIndex('shopify_products');
      vitisAlgoliaReady = true;
    } catch (error) {
      // Silent fail - Algolia not critical
    }
  }
})();

class VitisPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    // Only state variables in constructor
    this.debounceTimer = null;
    this.debounceDelay = 300;
    this.abortController = null;
    this.isOpen = false;
    this.input = null;
    this.resultsContainer = null;
    this.form = null;
    this.clearButton = null;
    this.loadingIndicator = null;
    this.scrollPosition = 0;
  }
  
  connectedCallback() {
    // Select elements - DOM is ready in connectedCallback
    this.input = this.querySelector('input[type="search"], input[name="q"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.form = this.querySelector('form');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.loadingIndicator = this.querySelector('[data-search-loading]');
    
    if (this.input && this.resultsContainer) {
      this.init();
    }
  }
  
  disconnectedCallback() {
    // Cleanup event listeners
    document.removeEventListener('click', this.boundOnClickOutside);
    document.removeEventListener('keydown', this.boundOnKeydown);
  }
  
  init() {
    this.bindEvents();
  }
  
  bindEvents() {
    // Input events
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    
    // ========================================
    // FIX SCROLL JUMP - Blocca COMPLETAMENTE lo scroll
    // ========================================
    
    this.storeHeader = document.querySelector('store-header');
    this.savedScrollPosition = 0;
    this.scrollLocked = false;
    
    // Funzione che previene qualsiasi scroll quando l'input è attivo
    this.preventScroll = () => {
      if (this.scrollLocked) {
        window.scrollTo(0, this.savedScrollPosition);
      }
    };
    
    // Add class for header animations on focus
    this.input.addEventListener('focus', () => {
      document.body.classList.add('predictive-search-open');
      
      // Salva posizione corrente
      this.savedScrollPosition = window.scrollY;
      
      // Blocca scroll
      this.scrollLocked = true;
      
      // Aggiungi listener che previene qualsiasi tentativo di scroll
      window.addEventListener('scroll', this.preventScroll, { passive: false });
      
      // Disabilita anche lo scroll handler del tema
      if (this.storeHeader) {
        this.storeHeader._scrollDisabled = true;
      }
    });
    
    // Remove class on blur
    this.input.addEventListener('blur', () => {
      setTimeout(() => {
        if (document.activeElement !== this.input && !this.resultsContainer.contains(document.activeElement)) {
          document.body.classList.remove('predictive-search-open');
          
          // Sblocca scroll
          this.scrollLocked = false;
          
          // Rimuovi listener
          window.removeEventListener('scroll', this.preventScroll);
          
          // Riabilita scroll handler tema
          if (this.storeHeader) {
            this.storeHeader._scrollDisabled = false;
          }
        }
      }, 150);
    });
    
    // Clear button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClear.bind(this));
    }
    
    // Update clear button visibility on initial load
    this.updateClearButton(this.input.value.length > 0);
    
    // Close on click outside (save reference for cleanup)
    this.boundOnClickOutside = this.onClickOutside.bind(this);
    document.addEventListener('click', this.boundOnClickOutside);
    
    // Close on ESC key (save reference for cleanup)
    this.boundOnKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.boundOnKeydown);
    
    // Prevent form submit if results are open
    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    
    // Handle add to cart in results
    this.resultsContainer.addEventListener('submit', this.onAddToCart.bind(this));
  }
  
  onInput(event) {
    const query = event.target.value.trim();
    
    // Update clear button visibility
    this.updateClearButton(query.length > 0);
    
    // Debounce the search
    clearTimeout(this.debounceTimer);
    
    if (query.length === 0) {
      this.close();
      return;
    }
    
    this.debounceTimer = setTimeout(() => {
      this.search(query);
    }, this.debounceDelay);
  }
  
  onFocus() {
    const query = this.input.value.trim();
    
    if (query.length > 0 && this.resultsContainer.innerHTML.trim() !== '') {
      this.open();
    } else if (query.length === 0) {
      // Show empty state
      this.showEmptyState();
      this.open();
    }
  }
  
  onClear(event) {
    event.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.updateClearButton(false);
    this.close();
  }
  
  onClickOutside(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }
  
  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.input.blur();
    }
  }
  
  onFormSubmit(event) {
    // Allow form to submit normally (goes to search page)
    // But close the dropdown
    this.close();
  }
  
  async onAddToCart(event) {
    const form = event.target;
    if (!form.matches('.predictive-search__product-form')) return;
    
    event.preventDefault();
    
    const button = form.querySelector('button');
    const originalHTML = button.innerHTML;
    
    // Show loading state
    button.innerHTML = `
      <svg class="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
        <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
      </svg>
    `;
    button.disabled = true;
    
    try {
      const formData = new FormData(form);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        // Show success state
        button.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        `;
        button.style.background = '#22c55e';
        
        // Update cart count if there's a cart counter
        this.updateCartCount();
        
        // Reset button after delay
        setTimeout(() => {
          button.innerHTML = originalHTML;
          button.style.background = '';
          button.disabled = false;
        }, 1500);
      } else {
        throw new Error('Add to cart failed');
      }
    } catch (error) {
      console.error('[Vitis Search] Add to cart error:', error);
      button.innerHTML = originalHTML;
      button.disabled = false;
    }
  }
  
  async search(query) {
    // Minimum query length
    if (query.length < 2) {
      return;
    }
    
    // Check Algolia readiness
    if (!vitisAlgoliaReady || !vitisAlgoliaIndex) {
      return;
    }
    
    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    
    this.abortController = new AbortController();
    
    // Show loading state
    this.showLoading();
    this.open();
    
    try {
      const { hits } = await vitisAlgoliaIndex.search(query, { 
        hitsPerPage: 5 
      });
      
      // Generate HTML from Algolia hits
      const generatedHTML = this.generateAlgoliaResultsHTML(hits, query);
      
      this.resultsContainer.innerHTML = generatedHTML;
      
      this.hideLoading();
      
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      
      this.hideLoading();
      this.showError();
    }
  }
  
  /**
   * Generate HTML from Algolia hits matching predictive-search.liquid structure
   */
  generateAlgoliaResultsHTML(hits, query) {
    // Format price helper
    const formatPrice = (price) => {
      if (!price && price !== 0) return '';
      const num = parseFloat(price);
      return num.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };
    
    // Get image URL with size
    const getImageUrl = (hit) => {
      if (hit.image) {
        // Handle Shopify CDN images - add size parameter
        if (hit.image.includes('cdn.shopify.com')) {
          return hit.image.replace(/(\.[^.]+)$/, '_200x200$1');
        }
        return hit.image;
      }
      return '';
    };
    
    // Generate product cards (max 5 to avoid scrolling)
    let productsHTML = '';
    const maxProducts = 5;
    
    if (hits.length > 0) {
      const productCards = hits.slice(0, maxProducts).map(hit => {
        const handle = hit.handle || hit.objectID;
        const productUrl = `/products/${handle}`;
        const imageUrl = getImageUrl(hit);
        const vendor = hit.vendor || hit.brand || '';
        const title = hit.title || hit.name || '';
        const price = hit.price || hit.variants_min_price || 0;
        const compareAtPrice = hit.compare_at_price || hit.variants_compare_at_price || 0;
        const available = hit.inventory_available !== false && hit.available !== false;
        const variantId = hit.variant_id || hit.variants_ids?.[0] || hit.objectID;
        
        const hasDiscount = compareAtPrice && compareAtPrice > price;
        
        // Image HTML
        const imageHTML = imageUrl 
          ? `<img src="${imageUrl}" alt="${title}" width="64" height="64" loading="lazy">`
          : `<div class="predictive-search__product-placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </div>`;
        
        // Price HTML
        let priceHTML = '';
        if (hasDiscount) {
          priceHTML = `
            <span class="predictive-search__product-price--old" style="font-weight: 500;">${formatPrice(compareAtPrice)}</span>
            <span class="predictive-search__product-price predictive-search__product-price--sale" style="font-weight: 500;">${formatPrice(price)}</span>
          `;
        } else {
          priceHTML = `<span class="predictive-search__product-price" style="font-weight: 500;">${formatPrice(price)}</span>`;
        }
        
        // Add to cart button HTML
        const buttonHTML = available 
          ? `<button type="button" class="predictive-search__add-btn" data-variant-id="${variantId}" aria-label="Aggiungi al carrello">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </button>`
          : `<button type="button" class="predictive-search__add-btn predictive-search__add-btn--disabled" disabled aria-label="Non disponibile">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </button>`;
        
        return `
          <div class="predictive-search__product">
            <a href="${productUrl}" class="predictive-search__product-image">
              ${imageHTML}
            </a>
            <div class="predictive-search__product-info">
              ${vendor ? `<p class="predictive-search__product-brand">${vendor}</p>` : ''}
              <a href="${productUrl}" class="predictive-search__product-title" style="font-weight: 600;">${title}</a>
              <div class="predictive-search__product-prices">
                ${priceHTML}
              </div>
            </div>
            ${buttonHTML}
          </div>
        `;
      }).join('');
      
      productsHTML = `
        <div class="predictive-search__products">
          <h3 class="predictive-search__section-title">Top Prodotti</h3>
          <div class="predictive-search__products-list">
            ${productCards}
          </div>
          <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__view-all">
            Vedi tutti i risultati per "${query}"
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"></path>
              <path d="m12 5 7 7-7 7"></path>
            </svg>
          </a>
        </div>
      `;
    } else {
      // No results
      productsHTML = `
        <div class="predictive-search__products">
          <div class="predictive-search__no-products">
            <div class="predictive-search__no-products-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
                <path d="M8 8l6 6"></path>
              </svg>
            </div>
            <h3 class="predictive-search__no-products-title">
              Nessun prodotto trovato per "<strong>${query}</strong>"
            </h3>
            <p class="predictive-search__no-products-text">
              Prova con termini diversi o esplora le collezioni
            </p>
            <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__view-all">
              Cerca comunque "${query}"
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </a>
          </div>
        </div>
      `;
    }
    
    // Full results container (without sidebar for Algolia - products only)
    return `
      <div id="predictive-search-results" class="predictive-search-results">
        <div class="predictive-search__grid" data-has-sidebar="false">
          ${productsHTML}
        </div>
      </div>
    `;
  }
  
  showEmptyState() {
    this.resultsContainer.innerHTML = `
      <div class="predictive-search-dropdown">
        <div class="predictive-search__empty">
          <div class="predictive-search__empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#a3b835" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>
          <p class="predictive-search__empty-title">Inizia a digitare per cercare prodotti...</p>
          <p class="predictive-search__empty-subtitle">Cerca per nome prodotto, brand o categoria</p>
        </div>
      </div>
    `;
  }
  
  showLoading() {
    // Show loading indicator
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'flex';
    }
    
    // Show skeleton in results - products only (sidebar content is unknown during loading)
    this.resultsContainer.innerHTML = `
      <div class="predictive-search-results">
        <div class="predictive-search__grid" data-has-sidebar="false">
          <div class="predictive-search__products">
            <div class="predictive-search__skeleton" style="height: 11px; width: 90px; margin-bottom: 16px;"></div>
            <div class="predictive-search__products-list">
              ${[1,2,3,4].map(() => `
                <div class="predictive-search__product" style="background: #f9fafb;">
                  <div class="predictive-search__skeleton" style="width: 64px; height: 64px; border-radius: 8px; flex-shrink: 0;"></div>
                  <div class="predictive-search__product-info">
                    <div class="predictive-search__skeleton" style="height: 8px; width: 50px; margin-bottom: 6px;"></div>
                    <div class="predictive-search__skeleton" style="height: 13px; width: 75%; margin-bottom: 6px;"></div>
                    <div class="predictive-search__skeleton" style="height: 13px; width: 55px;"></div>
                  </div>
                  <div class="predictive-search__skeleton" style="width: 40px; height: 40px; border-radius: 8px; flex-shrink: 0;"></div>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
  }
  
  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
    }
  }
  
  showError() {
    this.resultsContainer.innerHTML = `
      <div class="predictive-search-dropdown">
        <div class="predictive-search__empty">
          <div class="predictive-search__empty-icon" style="background: #fef2f2;">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <p class="predictive-search__empty-title">Si è verificato un errore</p>
          <p class="predictive-search__empty-subtitle">Riprova più tardi</p>
        </div>
      </div>
    `;
  }
  
  updateClearButton(show) {
    if (this.clearButton) {
      if (show) {
        this.clearButton.classList.add('is-visible');
      } else {
        this.clearButton.classList.remove('is-visible');
      }
    }
  }
  
  open() {
    if (!this.isOpen) {
      // Class is now managed by focus/blur events, not open/close
      this.resultsContainer.style.display = 'block';
      this.isOpen = true;
      this.setAttribute('open', '');
    }
  }
  
  close() {
    if (this.isOpen) {
      // Class is now managed by focus/blur events, not open/close
      this.resultsContainer.style.display = 'none';
      this.isOpen = false;
      this.removeAttribute('open');
    }
  }
  
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.style.display = '';
        }
      });
      
      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      
    } catch (error) {
      console.error('[Vitis Search] Cart update error:', error);
    }
  }
}

// Register custom element
if (!customElements.get('vitis-predictive-search')) {
  customElements.define('vitis-predictive-search', VitisPredictiveSearch);
}
